import generateUniqueId from "@/utils/generateUniqueId";
import { addDays, format, isBefore, startOfWeek } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface TimeSlot {
  id: string;
  startDate: Date;
  duration: 15 | 30 | 45 | 60;
  /** true when the slot is stored in the backend */
  persisted?: boolean;
  bookedByUser?: {
    userId: string;
    name: string;
    profileImageUrl?: string;
    createdAt?: Date;
    description?: string;
  } | null;
}

interface WeekCalendarProps {
  slots: TimeSlot[];
  slotDuration: TimeSlot["duration"];
  isLoading?: boolean;
  maxHeight?: number;
  /**
   * Called when the user finishes a selection – may contain 1‑N new slots
   */
  onAdd: (slots: TimeSlot[]) => void;
  /**
   * Called when the user removes a slot
   */
  onDelete: (slotId: string) => void;
  /** Start of the initial week (Mon) */
  initialWeekStart?: Date;
  onWeekChange?: (weekStart: Date) => void;
}

export function WeekCalendar({
  slots,
  slotDuration,
  onAdd,
  onDelete,
  isLoading = false,
  maxHeight = 600,
  onWeekChange,
  initialWeekStart,
}: WeekCalendarProps) {
  const [weekStart, setWeekStart] = useState<Date>(
    () => initialWeekStart ?? startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selection, setSelection] = useState<{
    day: number;
    startMins: number; // minutes from 00:00
    endMins: number;
  } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [visibleDays, setVisibleDays] = useState(7);

  const bodyRef = useRef<HTMLDivElement>(null);
  const nowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const DAYS = visibleDays;
  const HOURS_START = 6;
  const HOURS_END = 22;

  const dayLabels = useMemo(() => Array.from({ length: DAYS }).map((_, i) => addDays(weekStart, i)), [weekStart, DAYS]);

  // Slots lookup – O(1) by timestamp prevents nested loops in render
  const slotMap = useMemo(() => {
    const map = new Map<number, TimeSlot>();
    slots.forEach((s) => {
      const key = s.startDate.getTime();
      map.set(key, s);
    });
    return map;
  }, [slots]);

  const hourRows = useMemo(() => Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, i) => HOURS_START + i), []);

  // Check if viewport is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setVisibleDays(mobile ? 3 : 7);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Helpers
  const makeKey = (d: number, h: number, m: number) => `${d}-${h}-${m}`;

  const cellDate = useCallback(
    (d: number, h: number, m: number) => {
      const dt = addDays(weekStart, d);
      dt.setHours(h, m, 0, 0);
      return dt;
    },
    [weekStart]
  );

  const slotAt = useCallback(
    (d: number, h: number, m: number): TimeSlot | undefined => {
      const dt = cellDate(d, h, m).getTime();
      return slotMap.get(dt);
    },
    [cellDate, slotMap]
  );

  const isPast = (d: number, h: number, m: number) => isBefore(cellDate(d, h, m), new Date());

  // Selection logic
  const beginSelection = (d: number, h: number, m: number) => {
    if (isPast(d, h, m) || slotAt(d, h, m)) return; // ignore invalid
    const mins = h * 60 + m;
    setSelection({ day: d, startMins: mins, endMins: mins + (slotDuration - 1) });
    setIsSelecting(true);
  };

  const extendSelection = (d: number, h: number, m: number) => {
    if (!selection || d !== selection.day || !isSelecting) return;
    const mins = h * 60 + m;
    if (mins < selection.startMins) return; // only forward drag
    // ensure no intersecting slots inside new range
    for (let t = selection.startMins; t <= mins; t += 15) {
      const hd = Math.floor(t / 60);
      const md = t % 60;
      if (slotAt(d, hd, md)) return;
    }
    setSelection({ ...selection, endMins: mins });
  };

  const commitSelection = () => {
    if (!selection) return;
    setIsSelecting(false);

    const { day, startMins, endMins } = selection;
    const total = endMins - startMins + 15; // inclusive mins length
    const count = Math.floor(total / slotDuration);
    if (count <= 0) return;

    const created: TimeSlot[] = [];
    for (let i = 0; i < count; i++) {
      const mins = startMins + i * slotDuration;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const date = cellDate(day, h, m);
      // skip overlap safety – shouldn't happen but double‑check
      if (slotMap.has(date.getTime())) continue;
      created.push({ id: generateUniqueId(), startDate: date, duration: slotDuration });
    }

    if (created.length) onAdd(created);
    setSelection(null);
  };

  // Touch handling for mobile
  const handleTouchStart = (d: number, h: number, m: number, e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);

    // Delay selection start to distinguish between scroll and select
    const timer = setTimeout(() => {
      if (touchStartY !== null) {
        beginSelection(d, h, m);
      }
    }, 150);

    return () => clearTimeout(timer);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartY) return;

    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - touchStartY);

    // If significant vertical movement, cancel selection and allow scroll
    if (deltaY > 10 && !isSelecting) {
      setTouchStartY(null);
      return;
    }

    // Otherwise handle as selection if we've started selecting
    if (isSelecting && bodyRef.current) {
      // Get position relative to calendar
      const rect = bodyRef.current.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top + bodyRef.current.scrollTop;

      // Calculate which cell this corresponds to
      const cellWidth = rect.width / (DAYS + 1); // +1 for time column
      const cellHeight = 20; // height of a 15-min cell

      const day = Math.floor(x / cellWidth) - 1; // -1 for time column
      const totalMins = Math.floor(y / cellHeight) * 15 + HOURS_START * 60;
      const hour = Math.floor(totalMins / 60);
      const minute = totalMins % 60;

      if (day >= 0 && day < DAYS) {
        extendSelection(day, hour, minute);
      }
    }
  };

  const handleTouchEnd = () => {
    if (isSelecting) {
      commitSelection();
    }
    setTouchStartY(null);
  };

  //Week navigation
  const shiftWeek = (dir: -1 | 1) => {
    const next = addDays(weekStart, dir * 7);
    setWeekStart(next);
    onWeekChange?.(next);
  };

  // Day navigation for mobile view
  const shiftDays = (dir: -1 | 1) => {
    const next = addDays(weekStart, dir * visibleDays);
    setWeekStart(next);
    onWeekChange?.(next);
  };

  // Scroll to current time on mount
  useEffect(() => {
    if (!bodyRef.current || !nowRef.current) return;
    const nowH = new Date().getHours();
    if (nowH < HOURS_START || nowH > HOURS_END) return;
    const pos = nowRef.current.offsetTop;
    bodyRef.current.scrollTop = pos - bodyRef.current.clientHeight / 2;
  }, []);

  // Render helpers
  const renderCell = (d: number, h: number, m: number) => {
    const dt = cellDate(d, h, m);
    const slot = slotAt(d, h, m);
    const withinSel =
      selection && selection.day === d && h * 60 + m >= selection.startMins && h * 60 + m <= selection.endMins;
    const isCurrent = (() => {
      const n = new Date();
      return (
        n.getFullYear() === dt.getFullYear() &&
        n.getMonth() === dt.getMonth() &&
        n.getDate() === dt.getDate() &&
        n.getHours() === h &&
        n.getMinutes() >= m &&
        n.getMinutes() < m + 15
      );
    })();

    return (
      <div
        key={makeKey(d, h, m)}
        ref={isCurrent ? nowRef : undefined}
        onMouseDown={() => beginSelection(d, h, m)}
        onMouseOver={() => extendSelection(d, h, m)}
        onMouseUp={commitSelection}
        onTouchStart={(e) => handleTouchStart(d, h, m, e)}
        className={`relative h-5 border-r border-gray-200 ${m === 0 ? "border-t border-gray-300" : ""} ${
          slot
            ? slot.bookedByUser
              ? "bg-blue-200 cursor-default hover:bg-blue-300"
              : "bg-green-200 cursor-pointer hover:bg-green-300"
            : isPast(d, h, m)
            ? "bg-gray-50 cursor-default"
            : withinSel
            ? "bg-green-100 cursor-pointer hover:bg-green-200"
            : "bg-white cursor-pointer hover:bg-gray-50"
        } transition-colors duration-100`}
        title={format(dt, "h:mm a")}
        onClick={() => {
          if (slot && !slot.bookedByUser) onDelete(slot.id);
        }}
      >
        {isCurrent && <div className="absolute inset-x-0 top-0 h-1 bg-red-500 z-10" />}
        {slot && (
          <span className="absolute left-0 top-0 text-xs bg-green-600 text-white px-1 truncate max-w-full rounded-sm z-20">
            {format(slot.startDate, "h:mm")} {slot.bookedByUser && "👤"}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className="space-y-4 select-none"
      ref={containerRef}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {/* Calendar header */}
      <div className="flex items-center justify-between bg-white rounded-t shadow p-3">
        <button
          className="p-2 rounded-full hover:bg-gray-100 flex items-center justify-center w-8 h-8 transition-colors"
          onClick={() => (isMobile ? shiftDays(-1) : shiftWeek(-1))}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h2 className="font-medium text-center text-gray-800">
          {format(weekStart, "MMM d")} – {format(addDays(weekStart, DAYS - 1), "MMM d, yyyy")}
        </h2>
        <button
          className="p-2 rounded-full hover:bg-gray-100 flex items-center justify-center w-8 h-8 transition-colors"
          onClick={() => (isMobile ? shiftDays(1) : shiftWeek(1))}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 6L15 12L9 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-b shadow overflow-hidden">
        <div
          className="grid grid-cols-8 border-b text-center text-sm font-medium bg-gray-50 sticky top-0 z-10"
          style={{ gridTemplateColumns: `80px repeat(${DAYS}, 1fr)` }}
        >
          <div className="py-2 px-2 text-gray-500 font-medium">Time</div>
          {dayLabels.map((d) => (
            <div key={d.toISOString()} className="py-2 border-l border-gray-200">
              <div className="font-medium text-gray-700">{format(d, "EEE")}</div>
              <div className="text-xs text-gray-500">{format(d, "MMM d")}</div>
            </div>
          ))}
        </div>

        <div
          ref={bodyRef}
          style={{ maxHeight }}
          className={`relative overflow-y-auto ${isLoading ? "opacity-60 pointer-events-none" : ""} ${
            isSelecting ? "touch-none" : ""
          }`}
        >
          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
              <div className="flex items-center space-x-2">
                <svg
                  className="animate-spin h-5 w-5 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Loading calendar...</span>
              </div>
            </div>
          )}

          <div className="grid" style={{ gridTemplateColumns: `80px repeat(${DAYS}, 1fr)` }}>
            {/* Time labels */}
            <div className="border-r border-gray-300 bg-gray-50 sticky left-0">
              {hourRows.map((h) => (
                <div key={h} className="h-20 border-b border-gray-300 relative">
                  <div className="absolute h-full w-full flex items-start justify-center">
                    <div className="mt-1 px-2 py-1 text-sm font-medium text-gray-700 flex flex-col items-center">
                      <span>
                        {h === 0 ? "12" : h < 12 ? h : h === 12 ? "12" : h - 12}
                        <span className="text-xs text-gray-500 ml-0.5">{h < 12 ? "AM" : "PM"}</span>
                      </span>
                      <div className="mt-0.5 w-10 border-t border-gray-300"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {Array.from({ length: DAYS }).map((_, d) => (
              <div key={d} className="border-l border-gray-200">
                {hourRows.map((h) => (
                  <div key={h} className="h-20 border-b border-gray-300 grid grid-rows-4">
                    {[0, 15, 30, 45].map((m) => renderCell(d, h, m))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 text-xs text-gray-600">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-200 border border-green-300 rounded-sm mr-1"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-200 border border-blue-300 rounded-sm mr-1"></div>
          <span>Booked</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-50 border border-gray-200 rounded-sm mr-1"></div>
          <span>Past</span>
        </div>
        <div className="flex items-center">
          <div className="w-5 h-1 bg-red-500 rounded-sm mr-1"></div>
          <span>Current time</span>
        </div>
      </div>

      {/* Mobile instructions */}
      {isMobile && (
        <div className="text-xs text-gray-500 mt-2 text-center">
          Tap and drag to create slots. Tap existing slots to remove them.
        </div>
      )}
    </div>
  );
}
